/**
 * Hook useCalendarUnifiedEvents
 *
 * Lê eventos unificados do Calendário Global via RPC get_calendar_unified_events.
 * Aplica filtros por intervalo, calendários e tipos de fonte (manual/booking/
 * activity/task/service_order/google).
 *
 * RELEASE:
 *   2026-10-09 v1.1.0 - Adiciona Ordem de Serviço como fonte oficial.
 *   2026-06-24 v1.0.0 - Versão inicial (Fase 2 Calendário Global)
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useCalendarUnifiedEvents');

export type CalendarEventSourceType =
  | 'manual'
  | 'booking'
  | 'activity'
  | 'task'
  | 'service_order'
  | 'google'
  | 'scheduled_message';

export interface UnifiedCalendarEvent {
  id: string;
  calendar_id: string;
  calendar_name: string;
  calendar_color: string;
  source_type: CalendarEventSourceType;
  source_id: string | null;
  google_event_id: string | null;
  title: string;
  description: string | null;
  /** Descrição em HTML (rich text) — eventos manuais */
  description_html?: string | null;
  location: string | null;
  start_at: string;
  end_at: string;
  all_day: boolean;
  timezone: string;
  status: 'confirmed' | 'tentative' | 'cancelled';
  attendees: unknown;
  reminders: Array<{ method: string; minutes: number }> | null;
  recurrence_rule: string | null;
  meet_link: string | null;
  last_sync_origin: 'system' | 'google';
  user_permission: 'manage' | 'edit' | 'view' | 'free_busy';
  // v1.3.0 — campos enriquecidos para 'activity' e 'booking' (NULL para demais fontes)
  completed_at?: string | null;
  cancelled_at?: string | null;
  deal_id?: string | null;
  deal_title?: string | null;
  customer_name?: string | null;
  cancel_token?: string | null;
  host_user_name?: string | null;
  /** v1.4.0 — avatar do responsável (profiles.avatar_url) */
  host_avatar_url?: string | null;
  booking_status?: string | null;
  /** v1.7.0 — dono do calendário (para distinguir "Pessoal" de agentes distintos) */
  calendar_owner_id?: string | null;
  calendar_owner_name?: string | null;
}

interface UseUnifiedEventsParams {
  /** Início da janela (ISO) */
  start: string;
  /** Fim da janela (ISO) */
  end: string;
  /** Calendários selecionados; null = todos visíveis */
  calendarIds?: string[] | null;
  /** Filtro de fontes; null = todas */
  sourceTypes?: CalendarEventSourceType[] | null;
  /** Habilita/desabilita a query */
  enabled?: boolean;
  /**
   * v1.1.0: Mostrar eventos concluídos/cancelados (default TRUE).
   * Quando FALSE, a RPC filtra atividades/bookings/tasks já finalizados.
   */
  includeFinished?: boolean;
}

/**
 * Busca eventos unificados respeitando permissões via RPC.
 *
 * RELEASE:
 *   2026-07-24 v1.1.0 - Novo parâmetro includeFinished (toggle "Mostrar concluídos").
 *   2026-06-24 v1.0.0 - Versão inicial.
 */
export function useCalendarUnifiedEvents(params: UseUnifiedEventsParams) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const {
    start,
    end,
    calendarIds,
    sourceTypes,
    enabled = true,
    includeFinished = true,
  } = params;

  return useQuery({
    queryKey: [
      'calendar-unified-events',
      tenantId,
      start,
      end,
      calendarIds ?? 'all',
      sourceTypes ?? 'all',
      includeFinished,
    ],
    enabled: !!tenantId && !!start && !!end && enabled,
    staleTime: 30_000,
    queryFn: async () => {
      logger.info('Buscando eventos unificados', { start, end, includeFinished });

      const { data, error } = await supabase.rpc('get_calendar_unified_events', {
        p_start: start,
        p_end: end,
        p_calendar_ids: calendarIds ?? null,
        p_source_types: sourceTypes ?? null,
        p_timezone: 'America/Sao_Paulo',
        p_tenant_hint: tenantId ?? null,
        p_include_finished: includeFinished,
      } as never);

      if (error) {
        logger.error('Erro ao buscar eventos unificados', error);
        throw error;
      }

      return (data ?? []) as unknown as UnifiedCalendarEvent[];
    },
  });
}
